/**
 * LeagueDemoPanel — Admin → Demo tab (2026-09-28)
 * ─────────────────────────────────────────────────────────────────
 * Lets the super-admin switch a fully simulated League on and off to
 * show a prospect the whole league workflow with fictional data:
 *
 *   • pick what to simulate (completed season with playoffs + champion,
 *     a season in progress with a live game tonight, registration open
 *     for next season, an empty league for the setup walkthrough)
 *   • generate / rebuild / remove in one click
 *   • presenter links + a step-by-step walkthrough for the call
 *
 * Backend: GET/POST/DELETE /api/admin/demo/league (routes/admin-demo.js,
 * services/leagueDemo.js). Demo people use @demo.game-day.invalid
 * addresses (never emailed), card payments are simulated (Stripe is
 * never called) and every demo row is flagged is_demo, so Remove wipes
 * exactly the demo and nothing else.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import {
  FlaskConical, RefreshCw, Trash2, ExternalLink, Sparkles, Trophy,
  ClipboardList, CalendarDays, Globe, Users, PlayCircle, Wand2, Copy,
} from 'lucide-react';
import api from '../../services/api.js';
import { useAuth } from '../../context/AuthContext.jsx';

const T = {
  surface: 'var(--surface)', s2: 'var(--surface-2)', s3: 'var(--surface-3)',
  ice: 'var(--ice)', goal: 'var(--goal)', penalty: 'var(--penalty)', puck: 'var(--puck)',
  fire: 'var(--fire)', text: 'var(--text)', text2: 'var(--text-2)', text3: 'var(--text-3)',
  border: 'var(--border)', radius: 'var(--card-radius)',
};

const SCENARIOS = [
  {
    key: 'completed', icon: Trophy, title: 'Completed season — the end results',
    body: 'A full regular season with every scoresheet signed and locked, final standings, scoring leaders, a served suspension and a playoff bracket with a champion.',
  },
  {
    key: 'in_progress', icon: PlayCircle, title: 'Season in progress',
    body: 'Results so far, a live game tonight you score yourself (you are the scorekeeper), upcoming games with officials assigned, an active suspension and the player pool.',
  },
  {
    key: 'registration', icon: ClipboardList, title: 'Registration open for next season',
    body: 'The sign-up form, waivers and payments in every state — paid, unpaid, confirmed, refunded, minors waiting on a parent — including people who signed an older waiver version.',
  },
  {
    key: 'blank', icon: Wand2, title: 'Empty league for the setup walkthrough',
    body: 'A second league with nothing in it, so you can show creating a season, divisions, venues, the registration form and waivers from scratch.',
  },
];

const card = (extra = {}) => ({
  background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius,
  boxShadow: 'var(--shadow-card)', padding: 20, marginBottom: 16, ...extra,
});
const label = {
  fontFamily: 'var(--font-display)', fontSize: 10, fontWeight: 700, letterSpacing: '0.18em',
  textTransform: 'uppercase', color: T.text2, display: 'block', marginBottom: 6,
};
const input = {
  width: '100%', padding: '9px 12px', borderRadius: 10, border: `1px solid ${T.border}`,
  background: T.s2, color: T.text, fontSize: 14, boxSizing: 'border-box',
};
const btn = (color = T.ice, solid = true) => ({
  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 10,
  border: `1px solid ${solid ? color : `${color}55`}`, background: solid ? color : 'transparent',
  color: solid ? '#000' : color, fontWeight: 800, fontSize: 13, cursor: 'pointer',
  fontFamily: 'var(--font-display)', letterSpacing: '0.06em', textTransform: 'uppercase',
});

function Toggle({ checked, onChange, title, body, icon: Icon }) {
  return (
    <label style={{
      display: 'flex', gap: 12, alignItems: 'flex-start', padding: 12, borderRadius: 12, cursor: 'pointer',
      border: `1px solid ${checked ? 'var(--ice-a30, rgba(0,212,255,0.3))' : T.border}`,
      background: checked ? 'var(--ice-a10, rgba(0,212,255,0.08))' : T.s2,
    }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
             style={{ marginTop: 3, width: 16, height: 16, flexShrink: 0 }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: T.text, display: 'flex', alignItems: 'center', gap: 6 }}>
          {Icon && <Icon size={14} color="var(--ice)" />}{title}
        </div>
        {body && <div style={{ fontSize: 12.5, color: T.text2, marginTop: 3, lineHeight: 1.45 }}>{body}</div>}
      </div>
    </label>
  );
}

function LinkChip({ to, children, external, icon: Icon = ExternalLink }) {
  const style = {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 10,
    background: T.s2, border: `1px solid ${T.border}`, color: T.text, textDecoration: 'none',
    fontSize: 13, fontWeight: 600,
  };
  if (!to) return null;
  if (external) {
    return <a href={to} target="_blank" rel="noopener noreferrer" style={style}><Icon size={13} />{children}</a>;
  }
  return <Link to={to} style={style}><Icon size={13} />{children}</Link>;
}

/** Build every presenter link from the last run's summary. */
function linksFrom(summary) {
  if (!summary) return {};
  const s = summary.seasons || {};
  const L = summary.main_league_id;
  const q = (id) => (id ? `?season=${id}` : '');
  const base = L ? `/league/${L}` : null;
  return {
    dashboard: base && `${base}/dashboard`,
    registrations: base && s.registration && `${base}/registration${q(s.registration.id)}`,
    regSetup: base && `${base}/registration${q((s.registration || s.in_progress || {}).id)}`,
    publicSite: summary.links?.public_site,
    publicRegister: summary.links?.public_register,
    tonight: summary.links?.tonight_game,
    schedule: base && s.in_progress && `${base}/schedule${q(s.in_progress.id)}`,
    nextSchedule: base && s.registration && `${base}/schedule${q(s.registration.id)}`,
    officials: base && s.in_progress && `${base}/officials${q(s.in_progress.id)}`,
    players: base && `${base}/players${q((s.in_progress || s.completed || {}).id)}`,
    liveStandings: base && s.in_progress && `${base}/standings${q(s.in_progress.id)}`,
    finalStandings: base && s.completed && `${base}/standings${q(s.completed.id)}`,
    playoffs: base && s.completed && `${base}/playoffs${q(s.completed.id)}`,
    publicPlayoffs: summary.links?.public_site && s.completed && `${summary.links.public_site}/playoffs`,
    blank: summary.links?.blank_league,
    demoTeam: summary.demo_team?.id && `/team/${summary.demo_team.id}/games`,
    publicSiteEditor: base && `${base}/public-site`,
    chat: base && `${base}/chat`,
  };
}

function Walkthrough({ summary }) {
  const l = linksFrom(summary);
  const s = summary?.seasons || {};
  const steps = [];
  if (l.publicSite) {
    steps.push({
      title: 'Start where players start — the league website',
      body: 'News, standings, schedule, team pages, stats and sponsors, all updated from the game sheets. Point out the Register button.',
      links: [{ to: l.publicSite, text: 'Public site', external: true }],
    });
  }
  if (s.registration && l.publicRegister) {
    steps.push({
      title: 'Register a player the way a parent would',
      body: 'Fill in the form, tick each waiver and type the signature, then pay. The card step is simulated in the demo — in a real league it is Stripe Checkout and the money lands in the league\'s own Stripe account.',
      links: [{ to: l.publicRegister, text: 'Registration page', external: true }],
    });
    steps.push({
      title: 'See it arrive on the league side',
      body: 'Open any registration to show the answers, each signed waiver with the version, time, IP and signature, and the payment trail. Export everything to a spreadsheet, confirm players, then show the player pool.',
      links: [{ to: l.registrations, text: 'Registrations' }, { to: l.players, text: 'Player pool' }],
    });
  }
  if (l.blank) {
    steps.push({
      title: 'Set a league up from scratch',
      body: 'In the empty league: create a season, add divisions and a venue with its weekly ice times, build the registration form (dropdowns, private questions like emergency contacts), add waivers, set the fee and the opening dates, publish, and switch on the public site in Settings. Teams join by email invite — invite yourself to show that step.',
      links: [{ to: l.blank, text: 'Empty league' }],
    });
  }
  if (l.nextSchedule) {
    steps.push({
      title: 'Build next season\'s schedule',
      body: 'Next season already has its six teams enrolled and no games. Generate a round-robin from the venue\'s ice times, review the preview, and publish it — every coach is notified.',
      links: [{ to: l.nextSchedule, text: 'Schedule — next season' }],
    });
  }
  if (s.in_progress) {
    steps.push({
      title: 'Run game night',
      body: 'Tonight\'s game: both teams\' lineups are in. Add a couple of goals and a penalty, finalize both sheets, then sign as scorekeeper and for the referee — the scoresheet locks and the standings update on their own.',
      links: [{ to: l.tonight, text: 'Tonight\'s game' }, { to: l.liveStandings, text: 'Standings' }],
    });
    steps.push({
      title: 'Officials and schedule',
      body: 'Bulk-assign a referee crew to a whole Sunday or a date range, flip the schedule to the calendar view, and show the coaches\' channel.',
      links: [{ to: l.officials, text: 'Officials' }, { to: l.schedule, text: 'Schedule' }, { to: l.chat, text: 'Coaches\' channel' }],
    });
  }
  if (s.completed) {
    steps.push({
      title: 'The end results',
      body: `Final standings, scoring leaders (regular season and playoffs), discipline history and the bracket${summary?.champion ? ` — ${summary.champion} won the cup` : ''}.`,
      links: [{ to: l.finalStandings, text: 'Final standings' }, { to: l.playoffs, text: 'Playoffs' }, { to: l.publicPlayoffs, text: 'Public bracket', external: true }],
    });
  }
  if (l.demoTeam) {
    steps.push({
      title: 'What a coach sees',
      body: `You are listed as a coach of ${summary.demo_team.name}. On their Schedule page the League games card lists home and away games with a link to each game sheet — that is where coaches enter lineups. The team stays in your team switcher until you remove the demo.`,
      links: [{ to: l.demoTeam, text: summary.demo_team.name }],
    });
  }
  if (l.dashboard) {
    steps.push({
      title: 'The manager\'s weekly email',
      body: 'On the league dashboard, "Email me a sample" sends you the summary managers get: new registrations, payments to confirm, open scoresheets, games without officials, suspensions.',
      links: [{ to: l.dashboard, text: 'League dashboard' }],
    });
  }
  if (!steps.length) return null;
  return (
    <div style={card()}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Sparkles size={16} color="var(--ice)" />
        <div style={{ fontWeight: 800, fontSize: 16 }}>Walkthrough for the call</div>
      </div>
      <ol style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'grid', gap: 12 }}>
        {steps.map((st, i) => (
          <li key={st.title} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div style={{
              width: 26, height: 26, borderRadius: 13, background: 'var(--ice)', color: '#000', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13,
            }}>{i + 1}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700 }}>{st.title}</div>
              <div style={{ fontSize: 13, color: T.text2, margin: '3px 0 8px', lineHeight: 1.5 }}>{st.body}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {st.links.filter((x) => x.to).map((x) => (
                  <LinkChip key={x.text} to={x.to} external={x.external}>{x.text}</LinkChip>
                ))}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function LeagueDemoPanel() {
  const { loadUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);           // 'generate' | 'remove'
  const [unavailable, setUnavailable] = useState(null);
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/demo/league');
      setStatus(data);
      setUnavailable(null);
      setForm((f) => f || {
        ...(data.defaults || {}),
        ...(data.last_run?.status === 'active' ? data.last_run.options : {}),
        scenarios: {
          ...(data.defaults?.scenarios || {}),
          ...(data.last_run?.status === 'active' ? data.last_run.options?.scenarios : {}),
        },
      });
    } catch (err) {
      if (err?.response?.status === 503) setUnavailable(err.response.data?.error);
      else toast.error(err?.response?.data?.error || 'Could not load the demo status');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setScenario = (k, v) => setForm((f) => ({ ...f, scenarios: { ...f.scenarios, [k]: v } }));
  const anyScenario = form && Object.values(form.scenarios || {}).some(Boolean);

  const generate = async () => {
    if (!anyScenario) { toast.error('Pick at least one thing to simulate'); return; }
    if (status?.active && !window.confirm('Rebuild the demo? The current demo league, its teams and people are replaced with a fresh set.')) return;
    setBusy('generate');
    try {
      const payload = {
        league_name: form.league_name,
        teams: Number(form.teams), divisions: Number(form.divisions), roster_size: Number(form.roster_size),
        scenarios: form.scenarios, public_site: !!form.public_site, owner_as_coach: !!form.owner_as_coach,
      };
      const { data } = await api.post('/admin/demo/league', payload, { timeout: 120000 });
      setStatus(data.status);
      toast.success('Demo league is ready');
      loadUser?.();   // Leagues nav + the demo team in the switcher
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not generate the demo');
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!window.confirm('Remove the demo? Every demo league, team, player, game and registration is deleted. Real teams and leagues are not touched.')) return;
    setBusy('remove');
    try {
      const { data } = await api.delete('/admin/demo/league', { timeout: 120000 });
      setStatus(data.status);
      const r = data.removed || {};
      toast.success(`Demo removed — ${r.leagues || 0} leagues, ${r.teams || 0} teams, ${r.users || 0} people`);
      loadUser?.();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not remove the demo');
    } finally {
      setBusy(null);
    }
  };

  const summary = status?.active && status?.last_run?.status === 'active' ? status.last_run.summary : null;
  const links = useMemo(() => linksFrom(summary), [summary]);

  const copyLinks = async () => {
    const origin = window.location.origin;
    const lines = [
      links.publicSite && `League website: ${origin}${links.publicSite}`,
      links.publicRegister && `Registration page: ${origin}${links.publicRegister}`,
    ].filter(Boolean);
    try { await navigator.clipboard.writeText(lines.join('\n')); toast.success('Public links copied'); }
    catch { toast.error('Copy failed — select the links instead'); }
  };

  if (loading && !status) return <div style={card()}>Loading demo status…</div>;
  if (unavailable) {
    return (
      <div style={card({ borderColor: 'var(--puck)' })}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Demo mode is installing</div>
        <div style={{ color: T.text2, fontSize: 13 }}>{unavailable}</div>
        <button style={{ ...btn(T.ice, false), marginTop: 12 }} onClick={load}><RefreshCw size={13} /> Try again</button>
      </div>
    );
  }

  const run = status?.last_run;
  const counts = status?.counts || {};
  return (
    <div>
      {/* Status */}
      <div style={card({ borderColor: status?.active ? 'var(--goal)' : T.border })}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{
            width: 40, height: 40, borderRadius: 12, flexShrink: 0,
            background: status?.active ? 'rgba(52,211,153,0.12)' : T.s2,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <FlaskConical size={20} color={status?.active ? 'var(--goal)' : 'var(--text-2)'} />
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontWeight: 800, fontSize: 17 }}>
              {status?.active ? 'League demo is live' : 'No demo data in the system'}
            </div>
            <div style={{ fontSize: 13, color: T.text2, marginTop: 2 }}>
              {status?.active
                ? <>{(status.leagues || []).map((l) => l.name).join(' + ')} · {counts.teams} teams · {counts.users} fictional people
                    {run?.created_at && <> · built {formatDistanceToNow(new Date(run.created_at), { addSuffix: true })}</>}</>
                : 'Build a fictional league to show the whole league workflow — nothing touches real teams, and removing it wipes every demo row.'}
            </div>
          </div>
          {status?.active && (
            <button style={btn(T.penalty, false)} onClick={remove} disabled={!!busy}>
              <Trash2 size={14} /> {busy === 'remove' ? 'Removing…' : 'Remove demo'}
            </button>
          )}
        </div>
        {run?.status === 'failed' && (
          <div style={{ marginTop: 12, padding: 10, borderRadius: 10, background: 'rgba(248,113,113,0.1)', color: 'var(--penalty)', fontSize: 13 }}>
            The last build failed and was rolled back: {run.error || 'unknown error'}
          </div>
        )}
      </div>

      {/* Presenter links */}
      {summary && (
        <div style={card()}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <Globe size={16} color="var(--ice)" />
            <div style={{ fontWeight: 800, fontSize: 16, flex: 1 }}>Presenter links</div>
            {links.publicSite && (
              <button style={btn(T.ice, false)} onClick={copyLinks}><Copy size={13} /> Copy public links</button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <LinkChip to={links.dashboard} icon={Trophy}>League dashboard</LinkChip>
            <LinkChip to={links.registrations} icon={ClipboardList}>Registrations (next season)</LinkChip>
            <LinkChip to={links.tonight} icon={PlayCircle}>Tonight&apos;s game</LinkChip>
            <LinkChip to={links.schedule} icon={CalendarDays}>Schedule</LinkChip>
            <LinkChip to={links.officials} icon={Users}>Officials</LinkChip>
            <LinkChip to={links.finalStandings} icon={Trophy}>Final standings</LinkChip>
            <LinkChip to={links.playoffs} icon={Trophy}>Playoff bracket</LinkChip>
            <LinkChip to={links.blank} icon={Wand2}>Empty league (setup)</LinkChip>
            <LinkChip to={links.demoTeam} icon={Users}>{summary.demo_team?.name ? `${summary.demo_team.name} (your demo team)` : 'Demo team'}</LinkChip>
            <LinkChip to={links.publicSite} external>Public site</LinkChip>
            <LinkChip to={links.publicRegister} external>Public registration</LinkChip>
          </div>
          {summary.counts && (
            <div style={{ fontSize: 12.5, color: T.text2, marginTop: 12 }}>
              Generated {summary.counts.games} games ({summary.counts.locked_games} with signed, locked scoresheets),
              {' '}{summary.counts.registrations} registrations and {summary.counts.users} demo accounts
              {summary.champion ? <> · Champion: <strong style={{ color: T.text }}>{summary.champion}</strong></> : null}.
            </div>
          )}
        </div>
      )}

      {summary && <Walkthrough summary={summary} />}

      {/* Options */}
      {form && (
        <div style={card()}>
          <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 14 }}>
            {status?.active ? 'Rebuild with different options' : 'What to simulate'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 14 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <span style={label}>League name</span>
              <input style={input} value={form.league_name || ''} maxLength={90}
                     onChange={(e) => set('league_name', e.target.value)} />
            </div>
            <div>
              <span style={label}>Teams</span>
              <select style={input} value={form.teams} onChange={(e) => set('teams', e.target.value)}>
                {[4, 5, 6, 7, 8, 9, 10].map((n) => <option key={n} value={n}>{n} teams</option>)}
              </select>
            </div>
            <div>
              <span style={label}>Divisions</span>
              <select style={input} value={form.divisions} onChange={(e) => set('divisions', e.target.value)}>
                <option value={1}>One division</option>
                <option value={2}>Two divisions (6+ teams)</option>
              </select>
            </div>
            <div>
              <span style={label}>Players per team</span>
              <select style={input} value={form.roster_size} onChange={(e) => set('roster_size', e.target.value)}>
                {[10, 11, 12, 13, 14, 15, 16, 17, 18].map((n) => <option key={n} value={n}>{n} players</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gap: 10, marginBottom: 14 }}>
            {SCENARIOS.map((sc) => (
              <Toggle key={sc.key} icon={sc.icon} title={sc.title} body={sc.body}
                      checked={!!form.scenarios?.[sc.key]} onChange={(v) => setScenario(sc.key, v)} />
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10, marginBottom: 16 }}>
            <Toggle title="Public league website" icon={Globe}
                    body="League site with news, sponsors, standings and the public registration page."
                    checked={!!form.public_site} onChange={(v) => set('public_site', v)} />
            <Toggle title="Make me a coach of one demo team" icon={Users}
                    body="Shows the coach's side; the team sits in your team switcher until the demo is removed."
                    checked={!!form.owner_as_coach} onChange={(v) => set('owner_as_coach', v)} />
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <button style={btn(T.ice)} onClick={generate} disabled={!!busy || !anyScenario}>
              {busy === 'generate' ? <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Sparkles size={14} />}
              {busy === 'generate' ? 'Building…' : status?.active ? 'Rebuild demo' : 'Generate demo'}
            </button>
            <span style={{ fontSize: 12, color: T.text2 }}>Takes a few seconds.</span>
          </div>
          <div style={{ fontSize: 12, color: T.text2, marginTop: 14, lineHeight: 1.55 }}>
            Every name, team and score is invented. Demo people get <code>@demo.game-day.invalid</code> addresses,
            so no email or text is ever sent to them (your own copies, like the registration receipt, still reach you).
            Card payments in the demo are simulated — Stripe is never called. Scheduled reminders skip demo teams.
          </div>
        </div>
      )}
    </div>
  );
}
